'use client'

import { useRef } from 'react'
import { motion, useInView } from 'motion/react'
import { FaGithub, FaXTwitter, FaLinkedin, FaMugHot } from 'react-icons/fa6'

const Contact = () => {
	const ref = useRef(null)
	const isInView = useInView(ref, { once: false, amount: 0.2 })

	const containerVariants = {
		hidden: { opacity: 0 },
		visible: {
			opacity: 1,
			transition: { staggerChildren: 0.1 }
		}
	} as const

	const itemVariants = {
		hidden: { opacity: 0, y: 20 },
		visible: {
			opacity: 1,
			y: 0,
			transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] as const }
		}
	} as const

	const socialLinks = [
		{ name: 'GitHub', url: 'https://github.com/da4ndo', icon: FaGithub, color: 'hover:bg-[#333] hover:text-white' },
		{ name: 'X', url: 'https://x.com/da4ndo005', icon: FaXTwitter, color: 'hover:bg-black hover:text-white' },
		{ name: 'LinkedIn', url: 'https://www.linkedin.com/in/da4ndo', icon: FaLinkedin, color: 'hover:bg-[#0A66C2] hover:text-white' },
		{ name: 'Coffee', url: 'https://coff.ee/Da4ndo', icon: FaMugHot, color: 'hover:bg-[#FFDD00] hover:text-black' }
	]

	return (
		<section id="contact" className="py-24 md:py-32">
			<div className="section-container">
				<motion.div
					ref={ref}
					variants={containerVariants}
					initial="hidden"
					animate={isInView ? 'visible' : 'hidden'}
					className="max-w-2xl mx-auto text-center"
				>
					<motion.h2 variants={itemVariants} className="text-3xl md:text-5xl font-bold mb-6">
						Get in <span className="text-gradient">Touch</span>
					</motion.h2>

					<motion.p variants={itemVariants} className="text-lg text-gray-600 dark:text-gray-400 mb-12">
						I&apos;m always open to new opportunities. Feel free to reach out.
					</motion.p>

					{/* Contact cards */}
					<motion.div variants={itemVariants} className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-12">
						<motion.a
							href="mailto:contact@da4ndo.com"
							className="p-6 rounded-2xl text-center group bg-white border border-gray-200 shadow-md dark:glass-card dark:border-(--glass-border) dark:bg-(--glass-bg) dark:shadow-none"
							whileHover={{ y: -2 }}
						>
							<div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-4 group-hover:bg-green-500 transition-colors">
								<svg className="w-5 h-5 text-green-600 dark:text-green-400 group-hover:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
								</svg>
							</div>
							<h3 className="font-semibold mb-1">Email</h3>
							<p className="text-sm text-green-600 dark:text-green-400">contact@da4ndo.com</p>
						</motion.a>

						<motion.a
							href="https://www.google.com/maps/place/Budapest/"
							target="_blank"
							rel="noopener noreferrer"
							className="p-6 rounded-2xl text-center group bg-white border border-gray-200 shadow-md dark:glass-card dark:border-(--glass-border) dark:bg-(--glass-bg) dark:shadow-none"
							whileHover={{ y: -2 }}
						>
							<div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-4 group-hover:bg-green-500 transition-colors">
								<svg className="w-5 h-5 text-green-600 dark:text-green-400 group-hover:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
								</svg>
							</div>
							<h3 className="font-semibold mb-1">Location</h3>
							<p className="text-sm text-green-600 dark:text-green-400">Budapest, Hungary</p>
						</motion.a>
					</motion.div>

					{/* Social links */}
					<motion.div variants={itemVariants} className="flex justify-center gap-3">
						{socialLinks.map((social) => (
							<motion.a
								key={social.name}
								href={social.url}
								target="_blank"
								rel="noopener noreferrer"
								className={`w-11 h-11 rounded-full bg-gray-100 dark:bg-[#1a1a1a] flex items-center justify-center text-gray-600 dark:text-gray-400 transition-all duration-150 ${social.color}`}
								whileHover={{ y: -2, scale: 1.05, transition: { duration: 0.15 } }}
								whileTap={{ scale: 0.95 }}
							>
								<span className="sr-only">{social.name}</span>
								<social.icon className="w-5 h-5" />
							</motion.a>
						))}
					</motion.div>
				</motion.div>
			</div>
		</section>
	)
}

export default Contact
